import Vue from 'vue';
import VueRouter from 'vue-router';
import VueBodyClass from 'vue-body-class';
import { EventBus } from '../vue';
import store, { types } from '../store';
import getFrontConfig from '../../common/getFrontConfig';
import EventBusConfig from '../config/eventBusConfig';

// eslint-disable-next-line
__webpack_public_path__ = window.FRONT_CONFIG.urls.staticJs;

const Account = () =>
  import(
    /* webpackChunkName: "theaccount.js" */ '../components/account/Account.vue'
  );
const ThePDP = () =>
  import(/* webpackChunkName: "thepdp.js" */ '../components/ThePDP.vue');
const TheProductCustomizer = () =>
  import(
    /* webpackChunkName: "theproductcustomizer.js" */ '../components/customizer/TheProductCustomizer.vue'
  );
const TheProductList = () =>
  import(
    /* webpackChunkName: "theproductlist.js" */ '../components/TheProductList.vue'
  );

const FRONT_CONFIG = getFrontConfig();

const {
  categoriesWithProducts,
  customizerUrls = [],
  urls: {
    accountShow,
    accountProfile,
    accountAddresses,
    accountOrders,
    accountPrivacy,
    accountLogout,
    accountReturns,
    accountHelp,
  },
} = FRONT_CONFIG;

const validCategoryIds = [
  ...categoriesWithProducts.pageURL,
  ...categoriesWithProducts.ID,
];

let isRouterDisabled = false;

/**
 * Return the regex for the id of category route
 * Important: we need a regex because the route name is watched in TheProductList,
 * so it's neccessary to get a matching "category" route.
 * Otherwise we should check here and in that component.
 * @param {string} validIds - category IDs
 * @returns {string} regex for category IDs
 */
const getUrlRegex = (validIds) => {
  if (validIds && validIds.length > 0) {
    return validIds.join('|');
  }
  // TODO: Fallback if there is no IDs defined, but router path is not case sensitive so 'Home-Show' will match
  return '[a-z-\\d]*';
};

const getAccountRoutes = () => [
  accountProfile,
  accountAddresses,
  `${accountOrders}/:orderNumber?`,
  accountPrivacy,
  accountLogout,
  `${accountReturns}/:returnNumber?`,
  accountHelp,
];

const routeNames = {
  account: 'account',
  category: 'category',
  customizer: 'customizer',
  product: 'product',
};

const routes = [
  {
    name: routeNames.account,
    path: `${accountShow}`,
    alias: getAccountRoutes(),
    components: { default: Account },
    props: true,
    meta: { bodyClass: 'body--account' },
  },
  {
    name: routeNames.product,
    /*
    Valid paths:
    [/] /s/byl-sg/202BAPN13.T1200.html
    [/] /s/byl-sg/red-cosmic-garden-scarf-202BAPN13.T1200.html
    [/] /s/byl-sg/white-na%C3%AFf-garden-scarf-202BAPN26.T1050.html
    [/] /s/byl-sg/white-naïf-garden-scarf-202BAPN26.T1050.html
    [/] /s/byl-cl/bolso-bandolera-s-nylon-acolchado-verde-neón-211BBMX1W.T2535.html
    [/] /ec_en/black-nylon-13.5%22-laptop-sleeve-231BBH715.T2000.html
    */
    path: '*/([\\w\\.\\-%À-ÿ]*-)?:pid(\\w+\\.\\w+).html',
    components: { default: ThePDP },
    props: true,
    meta: { bodyClass: 'body--product' },
  },
  {
    name: routeNames.category,
    path: `*/:id(${getUrlRegex(validCategoryIds)})`,
    components: { default: TheProductList },
    props: true,
    meta: { bodyClass: 'body--category' },
  },
];

if (customizerUrls.length) {
  const customizerRoute = {
    name: routeNames.customizer,
    path: `*(${getUrlRegex(customizerUrls)})`,
    components: { default: TheProductCustomizer },
    props: true,
    meta: { bodyClass: 'body--customizer' },
  };
  routes.push(customizerRoute);
}

Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  routes,
  clickHistoryBack: false,
  historyCount: 0,
  scrollBehavior(to, from, savedPosition) {
    if (isRouterDisabled) {
      return undefined;
    }
    if (savedPosition) {
      return savedPosition;
    }
    if (to.name === routeNames.category) {
      return {
        selector: `[data-page-url="${to.fullPath}"]`,
        behavior: 'smooth',
        x: 0,
        y: 0,
      };
    }
    return { x: 0, y: 0 };
  },
});

const vueBodyClass = new VueBodyClass(routes);

/**
 * Detect whether the current page is an error page
 * @returns {boolean} true if it is error page
 */
function isErrorPage() {
  return !!document.querySelector('.error-page');
}

/**
 * Detect whether the current page is a valid vue-router name
 * @param {Object} route - route object
 * @returns {boolean} true if it is valid
 */
function isValidVueRoute(route) {
  return Object.values(routeNames).includes(route.name);
}

let isFirstLoad = true;

router.beforeEach((to, from, next) => {
  /*
    Logic to handle history navigation.
    - First check if we have the value saved in window sessionStorage and update the vue store.
    - Then check if backButton is clicked, because we need to handle the navigation counter
    - If The counter less or eq to 1 the back button will not showing.
    - Other case, the back button will show.
  */
  const navigationHistoryCount =
    parseInt(window.sessionStorage.getItem('navigationHistoryCount'), 10) || 0;
  if (navigationHistoryCount > 0) {
    store.commit(types.navigation.setHistoryCount, navigationHistoryCount);
  }

  const storeHistoryCount = store.state.navigation.isBackButtonClicked
    ? store.state.navigation.historyCount - 1
    : store.state.navigation.historyCount + 1;

  if (store.state.navigation.isBackButtonClicked) {
    store.commit(types.navigation.setIsBackButtonClicked, false);
  }

  // save the value in the store and also in the sessionStorage
  store.commit(types.navigation.setHistoryCount, storeHistoryCount);
  window.sessionStorage.setItem('navigationHistoryCount', storeHistoryCount);

  if (isRouterDisabled) next();
  const comesFromRoutedPath = isValidVueRoute(from);
  const goesToRoutedPath = isValidVueRoute(to);
  const goToVueNavegation = !comesFromRoutedPath && goesToRoutedPath;
  const exitVueNavegation = !goesToRoutedPath;
  const shouldCleanMainContent =
    goToVueNavegation || exitVueNavegation || isErrorPage();
  if (!isFirstLoad && shouldCleanMainContent) {
    // Force to redirect to routed page when:
    //  - coming from non-routed page
    //  - coming from error page
    // Then, the main content will be cleaned. Otherwise vue app page will be
    // displayed after the current main content.
    // Ex.: login => category
    window.location.href = to.fullPath;
  } else {
    next();
  }
});

router.beforeEach((to, from, next) => {
  if (isRouterDisabled) next();
  /*
  Per-route vuex actions and mutations
  Not included in Per-Route Guard (beforeEnter hook of each route) because it
  does not detect route updating: navigation between two different categories in
  PLP (via menu) or two different PDPs (go to another PDP from Shop The Look).
  */
  if (to.name === routeNames.category) {
    if (!isFirstLoad) {
      store.dispatch(types.productList.loadNewListFromUrl, to.fullPath);
    }
    store.commit(types.productList.show);
  } else if (to.name === routeNames.product) {
    if (!isFirstLoad) {
      // first try to retrieve and update product with available data from product list
      // eslint-disable-next-line import/no-named-as-default-member
      const data = store.getters.availableProductDataInPLP(to.params.pid);
      store.commit(types.product.update, data);

      // async call to retrieve the detail product values
      store.dispatch(types.product.load, to.params.pid);
    }
    store.commit(types.productList.hide);
  } else if (to.name === routeNames.account) {
    store.commit(types.productList.hide);
  }
  if (!isFirstLoad) {
    EventBus.$emit('header-popup:hide');
  }
  next();
});

router.beforeEach((to, from, next) => {
  // Check if fullPageBanner must be shown while Vue Routing
  require('../../components/fullPageBanner').init();
  next();
});

router.afterEach((to, from) => {
  // Emit event on router change
  if (router.history.current.name) {
    EventBus.$emit(EventBusConfig.events.router.willNavigate, {
      to,
      from,
    });
  }
});

router.beforeResolve((to, from, next) => {
  isFirstLoad = false;
  next();
});

router.beforeEach((to, from, next) => {
  vueBodyClass.guard(to, next);
});

/**
 * Replace the current route of Vue Router without triggering any hook
 * or side effect.
 * @param {string} url - url to set as current
 */
function routerReplaceAndIgnore(url) {
  // Avoid NavigationDuplicated error
  if (router.currentRoute.fullPath === url) return;
  // Disable router hooks until resolving the url
  isRouterDisabled = true;
  router.replace(url).then(() => {
    isRouterDisabled = false;
  });
}

if (window?.Vue) {
  window.Vue.prototype.$vuerouter = router;
}

export { routerReplaceAndIgnore };

export default router;
